import type { CaptureErrorCode } from './types';

const FRIENDLY_MESSAGES: Readonly<Record<CaptureErrorCode, string>> = {
  unsupported: 'Screen sharing is not supported in this browser.',
  'insecure-context': 'Screen sharing requires a secure connection.',
  'permission-denied': 'Screen sharing permission was not granted.',
  'no-source': 'No shareable screen, window, or tab was available.',
  'source-unavailable': 'The selected screen could not be captured.',
  aborted: 'Screen sharing was cancelled before it started.',
  'invalid-state': 'Screen sharing must be started from an active browser tab.',
  'constraints-failed': 'The selected screen cannot satisfy the capture requirements.',
  'invalid-options': 'This browser rejected the screen-sharing request.',
  'missing-video-track': 'The browser did not provide a screen video track.',
  'capture-cancelled': 'The screen-sharing request was cancelled.',
  'capture-already-active': 'A screen is already being shared.',
  unknown: 'Screen sharing could not be started.',
};

export class CaptureError extends Error {
  readonly code: CaptureErrorCode;

  constructor(code: CaptureErrorCode, cause?: unknown) {
    super(FRIENDLY_MESSAGES[code], cause === undefined ? undefined : { cause });
    this.name = 'CaptureError';
    this.code = code;
  }
}

export function normalizeCaptureError(error: unknown): CaptureError {
  if (error instanceof CaptureError) {
    return error;
  }

  const name = getErrorName(error);
  switch (name) {
    case 'NotAllowedError':
      return new CaptureError('permission-denied', error);
    case 'NotFoundError':
      return new CaptureError('no-source', error);
    case 'NotReadableError':
      return new CaptureError('source-unavailable', error);
    case 'AbortError':
      return new CaptureError('aborted', error);
    case 'InvalidStateError':
      return new CaptureError('invalid-state', error);
    case 'OverconstrainedError':
      return new CaptureError('constraints-failed', error);
    case 'TypeError':
      return new CaptureError('invalid-options', error);
    case 'SecurityError':
      return new CaptureError('insecure-context', error);
    default:
      return new CaptureError('unknown', error);
  }
}

function getErrorName(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('name' in error)) {
    return undefined;
  }

  return typeof error.name === 'string' ? error.name : undefined;
}
